import {
  DeleteOutlined,
  EditOutlined,
  LinkOutlined,
  MoreOutlined,
  SwapOutlined
} from "@ant-design/icons";
import { useMutation } from "@apollo/client";
import { Button, Dropdown, Popover, Radio, Switch, Tag, Tooltip } from "antd";
import { message } from "utils/ciq-message";
import {
  AcceptanceStatus,
  ESubmittalStatus,
  EWorkflowStatusDataBlock,
  ErrorMessages,
  GanttActivityTypes,
  IntegrationAuthStates,
  IntegrationSystemNameMap,
  SourceNames,
  SubmittalStatusToStrMap,
  isGcProjectAdminRole
} from "constants/index";
import { ProjectContext } from "context/ProjectProvider";
import {
  SubscriptionContext,
  RoleSubscriptionEnum
} from "context/SubscriptionProvider";
import {
  forwardRef,
  ReactElement,
  useCallback,
  useContext,
  useMemo,
  useRef,
  useState
} from "react";
import { Link, useParams } from "react-router-dom";
import {
  MUTATION_CANCEL_PROJECT_USER_INVITATION,
  MUTATION_CANCEL_SUBSCRIPTION_INVITATION,
  MUTATION_RESEND_EMAIL_INVITE,
  MUTATION_SEND_ACC_PROJECT_LEVEL_EMAIL_INVITE,
  MUTATION_SEND_ACC_SUBSCRIPTION_LEVEL_EMAIL_INVITE
} from "services/graphQL/mutations";
import {
  formatUSCurrency,
  getProjectUserSources,
  getSubscriptionUserSources,
  sortAlphanumeric,
  sortLinkedItems
} from "utils/utils";
import "./cell-renders.css";
import { agGridCheckFieldEditable } from "components/submittal-details/helpers";
import { ColDef, GridApi, ICellRendererParams } from "ag-grid-enterprise";

import { DATE_FORMAT_MMDDYYYY, DateUtils } from "utils/dateutils";
import { isPreconstructionPhase } from "utils/project-home-path";
import DateBlockSubmittalImpactVisualisation from "components/date-block/submittal-impact-visualise";
import ArrowUpBox from "components/svg-icons/arrow-right-up-box-line";
import {
  CopyIcon,
  SubmittalCiqIcon,
  MaterialCiqIcon,
  ScheduleCiqIcon
} from "components/svg-icons";
import BidPackageCiqIcon from "components/svg-icons/bid-package-ciq-icon";
import DesignPackageCiqIcon from "components/svg-icons/design-package-ciq-icon";
import DesignScopeCiqIcon from "components/svg-icons/design-scope-ciq-icon";
import DefaultTag from "components/widgets/default-tag";
import InfoIconSvg from "assets/svg/i-icon.svg?raw";
import CriticalActivityIconSvg from "assets/svg/critical_activity.svg?raw";
import QuestionMarkIconSvg from "assets/svg/questionmark-icon.svg?raw";
import { CONDITIONAL_FORMATTING_HEADER_ICON } from "components/conditional-formatting/header-icon";
import CopyDBTemplate from "popups/copy-db-template";
import UserTag from "components/user-tag/user-tag";
import modal from "antd/lib/modal";
import ProcoreLogoIcon from "components/svg-icons/procore-logo-icon";
import {
  IntegrationType,
  canOpenIntegrationDetails
} from "pages/subscription-settings/integrations-tab-new/utils";
import AutodeskLogoIcon from "components/svg-icons/autodesk-logo-icon";
import OpcLogoIcon from "components/svg-icons/opc-logo-icon";
import AuthenticationExpiredIcon from "components/svg-icons/authentication-expired-icon";
import { OPC_INTEGRATION_SYSTEM } from "constants/integrations";
import { RiskLevelType } from "components/date-block/models";
import LinkIconLogPage from "components/svg-icons/link-icon-log-page";
import VoidSubmittalIcon from "components/svg-icons/void-submittal-icon";
import TickIcon from "components/svg-icons/tick-icon";
import ClockIcon from "components/svg-icons/clock-icon";
import TitleDescriptionTooltip from "components/widgets/title-description-tooltip";
import CriticalActivityIcon from "components/svg-icons/critical-activity-icon";
import CriticalActivityTag from "pages/schedule/schedule-page/critical-activity-tag";
import CriticalActivityIndicator from "components/links/critical-activity-indicator";
import DeletedSubmittalTag from "components/deleted-submittal-tag";
import OffsetIcon from "components/svg-icons/offset-icon";
import ActivityOffsetTag from "components/activity-offset-tag";
import type { CustomCellRendererProps } from "ag-grid-react";
import CommentIcon from "components/svg-icons/comment-icon";
import { FeatureTypes } from "entity-app/constants";
import MaterialCreateCiqIcon from "components/svg-icons/material-create-ciq-icon";
import MissingIcon from "components/svg-icons/missing-icon";
import { MaterialRequestDurationButton } from "components/request-durations/material-request-duration-button/material-request-duration-button";
import BIM360logo from "../../assets/image/bim360-logo.png";
import { useCIQMutation } from "hooks/ciq-gql-hooks";
import SubmittalMoveToTrackedIcon from "components/svg-icons/submittal-move-to-tracked-icon";
import SubmittalUntrackCiqIcon from "components/svg-icons/submittal-untrack-ciq-icon";

interface TruncatedTextProps {
  text: string;
  onMouseEvent: (
    mouseEventType: "enter" | "leave",
    isTruncated: boolean
  ) => void;
  className?: string;
  disableMouseEvent?: boolean;
  dataTestId?: string;
}

const TruncatedText = forwardRef<HTMLDivElement, TruncatedTextProps>(
  (
    {
      text,
      onMouseEvent,
      className = "",
      disableMouseEvent = false,
      dataTestId
    },
    ref
  ) => {
    const innerRef = useRef<HTMLDivElement | null>(null);

    const checkTruncation = useCallback(() => {
      let isTruncated = false;
      if (innerRef.current) {
        isTruncated =
          innerRef.current.scrollWidth > innerRef.current.clientWidth;
        return isTruncated;
      }
      return isTruncated;
    }, []);

    if (disableMouseEvent) {
      return (
        <div
          className={`tooltip-disabled truncated-text-container truncate ${className}`}
          data-testid={dataTestId}
        >
          {text}
        </div>
      );
    }

    return (
      <div
        data-testid={dataTestId}
        ref={(node) => {
          if (typeof ref === "function") {
            ref(node);
          } else if (ref) {
            ref.current = node;
          }
          innerRef.current = node;
        }}
        className={`truncated-text-container truncate ${className}`}
        onMouseEnter={() => {
          onMouseEvent("enter", checkTruncation());
        }}
        onMouseLeave={() => {
          // No measurement on leave: the flag is unused by the "leave" arm and
          // reading scrollWidth here forces a synchronous reflow on every
          // mouse-out, in every grid cell that renders this.
          onMouseEvent("leave", false);
        }}
      >
        {text}
      </div>
    );
  }
);
TruncatedText.displayName = "TruncatedText";

export function TruncatedTextWithTooltip(
  props: Readonly<{
    text: string | null | undefined;
    tooltipContent?: any;
    className?: string;
    disableTooltip?: boolean;
    dataTestId?: string;
    tooltipZIndex?: number;
  }>
) {
  const {
    text,
    tooltipContent,
    disableTooltip = false,
    className = "",
    dataTestId,
    tooltipZIndex
  } = props;
  const [showTooltip, setShowTooltip] = useState(false);

  // Derive the next open state from the event alone — never gate the close on
  // the rendered `showTooltip`. mouseover/mouseout are continuous-priority in
  // React, so the open scheduled by an "enter" is not flushed before a "leave"
  // dispatched in the same input task; that handler still closes over
  // `showTooltip === false`, an `else if (showTooltip)` guard drops the close,
  // and the pending open then commits. This popup is fully controlled (no
  // onOpenChange, no mouseLeaveDelay), so a dropped close latches it open with
  // nothing left to shut it. Triggers that resize out from under the cursor —
  // e.g. the material log's ID cell, whose hover-revealed "View Specs" button
  // collapses the text to 0px — make that ordering the common case.
  const handleMouseEvent = useCallback(
    (mouseEvent: string, isTruncated: boolean) => {
      setShowTooltip(mouseEvent === "enter" && isTruncated);
    },
    []
  );

  const isTooltipDisabled =
    typeof disableTooltip === "boolean" && disableTooltip === true;

  return (
    <Tooltip
      title={
        tooltipContent || <div className="text-xs p-0.5">{text}</div> || ""
      }
      open={showTooltip}
      placement="top"
      zIndex={tooltipZIndex}
      autoAdjustOverflow
      // The hover "jitter" (a vertical scrollbar flickering in and out) came from
      // this tooltip's antd v4-era machinery running under antd v6. On every hover
      // `destroyOnHidden` remounted the popup and a `setTimeout` + `hidden`
      // class toggled it `display:none -> block`, forcing antd v6's ResizeObserver
      // to re-align it. That churn momentarily grew the document past the
      // fixed-height (h-screen) viewport, so the window scrollbar appeared, shifted
      // the cell out from under the cursor, fired mouseLeave -> close -> reopen, and
      // looped. Keep the popup mounted and let antd position it in one pass;
      // pointer-events:none stops the (informational) popup from stealing the hover.
      styles={{ root: { pointerEvents: "none" } }}
    >
      <TruncatedText
        dataTestId={dataTestId}
        className={`max-w-full w-full ${className}`}
        text={text ?? ""}
        onMouseEvent={handleMouseEvent}
        disableMouseEvent={isTooltipDisabled}
      />
    </Tooltip>
  );
}

export function truncatedTextCellRenderer(params: any) {
  const colId = params.colDef?.colId || params.colDef?.field;
  return <TruncatedTextWithTooltip text={params.value} dataTestId={colId} />;
}

export function IdLinkComponent(props: any, disableTooltip?: boolean) {
  const { value, data, projectId, context } = props;
  const cellText =
    context.projectDetails?.spec_section && data.spec_no
      ? `${data.spec_no} - ${value}`
      : `${value}`;

  return (
    <Link
      key={data.id}
      className="gridIdLink"
      to={{
        pathname: `/project/${projectId}/submittals/${data.id}`
      }}
      data-testid="index-link-component"
    >
      <TruncatedTextWithTooltip
        text={cellText}
        disableTooltip={disableTooltip}
      />
    </Link>
  );
}

export function IdLinkComponentNewModel(props: any, disableTooltip?: boolean) {
  const { value, data, projectId, context } = props;

  if (!value) return "";

  const cellText =
    context.projectDetails?.spec_section && data.spec_section.length > 0
      ? `${data.spec_section[0].number} - ${value}`
      : `${value}`;

  return (
    <Link
      key={data.id}
      className="gridIdLink"
      to={{
        pathname: `/project/${projectId}/submittals/${data.id}`
      }}
      data-testid="index-link-component-new-model"
    >
      <TruncatedTextWithTooltip
        text={cellText}
        disableTooltip={disableTooltip}
      />
    </Link>
  );
}

export function MaterialIdLinkCellRenderer(params: any) {
  const { data, value, context } = params;
  return (
    <Link
      key={data.id}
      className="gridIdLink"
      to={{
        pathname: `/project/${context.projectId}/materials/${data.id}`
      }}
      data-testid="index-link-material-link"
    >
      <TruncatedTextWithTooltip text={value} />
    </Link>
  );
}

export function IdLinkProjectSumbittalListComponent(props: any) {
  const { value, data, disabled = false, showToolTipMsg, tooltipMsg } = props;

  if (disabled && showToolTipMsg) {
    return (
      <TruncatedTextWithTooltip text={value} tooltipContent={tooltipMsg} />
    );
  }
  if (disabled && !showToolTipMsg) {
    return <TruncatedTextWithTooltip text={value} disableTooltip />;
  }
  return (
    <Link
      key={data.id}
      to={{
        pathname: `project/${data.id}/dashboard`
      }}
      data-testid="index-link-project-submittal-list"
    >
      <TruncatedTextWithTooltip text={value} />
    </Link>
  );
}

export const dateCellRenderer = (params: any) => {
  const formattedDate = DateUtils.format(params.value);
  return (
    <div className="w-full flex items-center">
      <div className="grow min-w-0">
        <TruncatedTextWithTooltip text={formattedDate} />
      </div>
      <div className="px-1">
        <EditOutlined className="cell-edit-icon" />
      </div>
    </div>
  );
};

export const dueDateCellRenderer = (params: any, disableTooltip: boolean) => {
  const colId = params.colDef?.colId || params.colDef?.field;
  let cellValueClassnames = "";
  if (params.value) {
    const todayStartDateObject = DateUtils.dateTimeObj().startOf("day");
    const dueDateObject = DateUtils.dateTimeObj(
      params.value,
      DATE_FORMAT_MMDDYYYY
    );
    cellValueClassnames = dueDateObject.isBefore(todayStartDateObject)
      ? "date-block-date-risk"
      : "";
  }
  return (
    <div className="w-full flex items-center">
      <div className="grow min-w-0">
        <TruncatedTextWithTooltip
          text={params.value}
          className={cellValueClassnames}
          disableTooltip={disableTooltip}
          dataTestId={colId}
        />
      </div>
      <div className="px-1">
        <EditOutlined className="cell-edit-icon" />
      </div>
    </div>
  );
};

const dueDateHeaderTemplate = (title: string) => ({
  template: `<div class="ag-cell-label-container" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        ${CONDITIONAL_FORMATTING_HEADER_ICON}
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
          <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
          <span title='${title}' class="ag-header-icon info-icon">${InfoIconSvg}</span>
          <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
          <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
          <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
          <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
          <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
          ${CONDITIONAL_FORMATTING_HEADER_ICON}
        </div>
      </div>`
});

export const logHeaderTemplateWithQuestionMark = () => ({
  template: `<div class="ag-cell-label-container" role="presentation">
        <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
        <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
        ${CONDITIONAL_FORMATTING_HEADER_ICON}
        <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
          <span data-ref="eText" class="ag-header-cell-text" role="columnheader"></span>
          <span class="grow text-right"><span class="ag-header-icon -mt-0.5 mr-1">${QuestionMarkIconSvg}</span></span>
          <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
          <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
          <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
          <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
          <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
          ${CONDITIONAL_FORMATTING_HEADER_ICON}
        </div>
      </div>`
});

const criticalActivityHeaderTemplate = () => ({
  template: `<div class="ag-cell-label-container" role="presentation">
              <span data-ref="eMenu" class="ag-header-icon ag-header-cell-menu-button"></span>
              <span data-ref="eFilterButton" class="ag-header-icon ag-header-cell-filter-button"></span>
              ${CONDITIONAL_FORMATTING_HEADER_ICON}
              <div data-ref="eLabel" class="ag-header-cell-label" role="presentation">
                <span data-ref="eText" class="ag-header-cell-text hidden" role="columnheader"></span>
                <span title='' class="ag-header-icon info-icon">${CriticalActivityIconSvg}</span>
                <span data-ref="eSortOrder" class="ag-header-icon ag-sort-order"></span>
                <span data-ref="eSortAsc" class="ag-header-icon ag-sort-ascending-icon"></span>
                <span data-ref="eSortDesc" class="ag-header-icon ag-sort-descending-icon"></span>
                <span data-ref="eSortNone" class="ag-header-icon ag-sort-none-icon"></span>
                <span data-ref="eFilter" class="ag-header-icon ag-filter-icon"></span>
                ${CONDITIONAL_FORMATTING_HEADER_ICON}
              </div>
            </div>`
});

export const submittalDueDateHeaderComponent = (params: any) => {
  const { context } = params;
  const headerTitle = context?.submittalHeaderMap?.due_date || "Due Date";
  const projectCompletionTitle =
    context?.submittalHeaderMap?.projected_last_milestone_date ||
    "Projected Completion Date";
  const deadlineTitle =
    context?.submittalHeaderMap?.final_deadline_date || "Deadline";

  const camelCaseHeaderTitle = `${headerTitle} is the minimum of ${deadlineTitle} and ${projectCompletionTitle}. `;
  return dueDateHeaderTemplate(camelCaseHeaderTitle);
};

export const materialDueDateHeaderComponent = (params: any) => {
  const { context } = params;
  const headerTitle = context?.materialHeaderMap?.due_date || "Due Date";
  const projectCompletionTitle =
    context?.materialHeaderMap?.projected_last_milestone_date ||
    "Projected Completion Date";
  const deadlineTitle =
    context?.materialHeaderMap?.final_deadline_date || "Deadline";

  const camelCaseHeaderTitle = `${headerTitle} is the minimum of ${deadlineTitle} and ${projectCompletionTitle}. `;

  return dueDateHeaderTemplate(camelCaseHeaderTitle);
};

export const bidPackageDueDateHeaderComponent = (params: any) => {
  const { displayName, context } = params;
  const headerTitle = displayName || "Due Date";
  const projectCompletionTitle =
    context?.featureHeaderMap?.projected_last_milestone_date ||
    "Projected Completion Date";
  const deadlineTitle =
    context?.featureHeaderMap?.final_deadline_date || "Deadline";

  const camelCaseHeaderTitle = `${headerTitle} is the minimum of ${deadlineTitle} and ${projectCompletionTitle}. `;

  return dueDateHeaderTemplate(camelCaseHeaderTitle);
};

export const leadTimeHeaderComponent = () => {
  const title = "In Calendar days, including weekends and holidays.";
  return dueDateHeaderTemplate(title);
};

export const criticalActivityHeaderComponent = () => {
  return criticalActivityHeaderTemplate();
};

export const projectDateCellRenderer = (params: any) => {
  if (params.data?.status_id === AcceptanceStatus.ACTIVE) {
    return dateCellRenderer(params);
  }
  return null;
};

type ProjectRecordCount = {
  testId: string;
  label: string;
  count: number;
  Icon: (props: {
    size?: number;
    className?: string;
    opacity?: string;
  }) => ReactElement;
};

function getProjectRecordCounts(data: any): ProjectRecordCount[] {
  if (isPreconstructionPhase(data)) {
    return [
      {
        testId: "design-package-count",
        label: "Design Packages",
        count: data?.design_package_count ?? 0,
        Icon: DesignPackageCiqIcon
      },
      {
        testId: "design-scope-count",
        label: "Design Scope Items",
        count: data?.design_scope_count ?? 0,
        Icon: DesignScopeCiqIcon
      },
      {
        testId: "bid-package-count",
        label: "Bid Packages",
        count: data?.bid_package_count ?? 0,
        Icon: BidPackageCiqIcon
      }
    ];
  }

  return [
    {
      testId: "submittals-count",
      label: "Submittals",
      count: data?.submittals_count ?? 0,
      Icon: SubmittalCiqIcon
    },
    {
      testId: "materials-count",
      label: "Materials",
      count: data?.materials_count ?? 0,
      Icon: MaterialCiqIcon
    },
    {
      testId: "schedules-count",
      label: "Schedule Versions",
      count: data?.schedules_count ?? 0,
      Icon: ScheduleCiqIcon
    }
  ];
}

export const projectOverviewCellRenderer = ({ data }: any) => {
  const recordCounts = getProjectRecordCounts(data);

  const showNumber = (num: number = 0) => (
    <div className={num >= 1000 ? "text-xs" : "text-sm"}>{num}</div>
  );

  function CountDetail({ num, icon }: { num: number; icon: ReactElement }) {
    return (
      <div className="flex items-center space-x-1 w-12">
        {icon} {showNumber(num)}
      </div>
    );
  }

  const popOver = (
    <div className="flex-col w-56 text-xs space-y-1">
      {recordCounts.map((item) => (
        <div className="flex justify-between w-full" key={item.testId}>
          <div
            className="flex items-center"
            data-testid={`${item.testId}-parent`}
          >
            <item.Icon />
            <div className="pl-1 opacity-60"> {item.label}</div>
          </div>
          <div className="font-medium" data-testid={item.testId}>
            {item.count}
          </div>
        </div>
      ))}
    </div>
  );

  return (
    <div className="p-2">
      <Popover placement="right" content={popOver} trigger="hover">
        <div className="flex justify-evenly items-center pl-1 h-7 space-x-1 border border-solid border-ciq-gray-300 rounded-sm opacity-80">
          {recordCounts.map((item) => (
            <CountDetail
              key={item.testId}
              num={item.count}
              icon={<item.Icon />}
            />
          ))}
        </div>
      </Popover>
    </div>
  );
};

export function CellChilderenRendererWithEditButton(props: any) {
  const { onEditCell, children } = props;
  return (
    <div className="w-full max-w-full overflow-hidden flex items-center">
      <div className="grow min-w-0 box-content overflow-hidden mr-1">
        {children ?? ""}
      </div>
      <div className="w-[24px] text-right">
        <EditOutlined
          className="cell-edit-icon"
          onClick={() => {
            if (onEditCell) {
              onEditCell();
            }
          }}
        />
      </div>
    </div>
  );
}
export function CellValueRendererWithEditIcon(
  params: CustomCellRendererProps,
  disableTooltip: boolean
) {
  const { value, valueFormatted, context, column, data, node, colDef } = params;
  const text = valueFormatted || value;

  const editDiv = (
    <EditOutlined
      className="cell-edit-icon"
      onClick={() => {
        if (context.onEditCell)
          context.onEditCell({
            rowIndex: node.rowIndex!,
            colKey: column!.getId(),
            data
          });
      }}
    />
  );

  if (!["string", "number"].includes(typeof text))
    return (
      <div className="w-full max-w-full h-full overflow-hidden flex items-start">
        <div className="grow min-w-0" />
        <div>{editDiv}</div>
      </div>
    );

  const colId = colDef?.colId || colDef?.field;
  return (
    <div className="w-full max-w-full h-full overflow-hidden flex items-start">
      <div className="grow min-w-0">
        <TruncatedTextWithTooltip
          text={text}
          disableTooltip={disableTooltip}
          dataTestId={colId}
        />
      </div>
      <div>{editDiv}</div>
    </div>
  );
}

export function CellCurrencyRendererWithEditIcon(params: any) {
  const { value, context, node, column, data } = params;
  return (
    <div className="w-full flex items-center">
      <TruncatedTextWithTooltip text={formatUSCurrency(value)} />
      <div>
        <EditOutlined
          className="cell-edit-icon"
          onClick={() => {
            if (context.onEditCell)
              context.onEditCell({
                rowIndex: node.rowIndex!,
                colKey: column!.getId(),
                data
              });
          }}
        />
      </div>
    </div>
  );
}

function EditableCell({ children }: { children?: React.ReactNode }) {
  return (
    <div className="flex">
      <div className="grow overflow-hidden truncate">{children ?? ""}</div>
      <div className="w-[13px]">
        <EditOutlined className="cell-edit-icon" />
      </div>
    </div>
  );
}

export function specSectionLabel(props: {
  specNumber?: string | number | null;
  specName?: string | null;
}): string {
  const normalize = (value?: string | number | null): string => {
    if (value === null || value === undefined) return "";
    return String(value).trim();
  };

  const specNumberStr = normalize(props.specNumber);
  const specNameStr = normalize(props.specName);

  if (specNumberStr && specNameStr) {
    return `${specNumberStr} - ${specNameStr}`;
  }

  return specNumberStr || specNameStr;
}

export function specSectionRenderer(params: any, disableTooltip?: boolean) {
  const { data, specNoField, specNameField, context } = params;
  const isTooltipDisabled =
    typeof disableTooltip === "boolean" && disableTooltip === true;
  try {
    const specSectionStr = specSectionLabel({
      specNumber: data[specNoField],
      specName: data[specNameField]
    });

    return (
      <div className="w-full h-full flex items-center">
        <div className="grow truncate  items-center">
          <TruncatedTextWithTooltip
            text={specSectionStr}
            disableTooltip={isTooltipDisabled}
          />
        </div>
        <div>
          <EditOutlined
            className="cell-edit-icon"
            onClick={() => {
              context.onEditCell({
                rowIndex: params.node.rowIndex!,
                colKey: params.column!.getId(),
                data
              });
            }}
          />
        </div>
      </div>
    );
  } catch {
    return "";
  }
}

export function userNameRenderer(params: any) {
  const { data } = params;
  try {
    let userNameStr = data?.user?.first_name || "";
    if (data?.user?.last_name) {
      userNameStr += `  ${data?.user?.last_name}`;
    }
    return (
      <div className="w-full flex">
        <TruncatedTextWithTooltip text={userNameStr} />
        <div>
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch {
    return "";
  }
}

export function userRoleRenderer(params: any) {
  const { data } = params;
  try {
    const role = data?.subscription_permission?.name || "";

    return (
      <div className="w-full flex">
        <TruncatedTextWithTooltip text={role} />
        <div>
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch {
    return "";
  }
}

export function userCompanyRenderer(params: any) {
  const { data } = params;
  try {
    const company = data?.subscription_vendor?.name || "";

    return (
      <div className="w-full flex">
        <TruncatedTextWithTooltip text={company} />
        <div>
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch {
    return "";
  }
}

export function projectUserRoleRenderer(params: any) {
  const { data } = params;
  try {
    const role = data.project_role?.name || "";

    return (
      <div className="w-full flex">
        <TruncatedTextWithTooltip text={role} />
        <div>
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch {
    return "";
  }
}

export function projectCompanyRoleRenderer(params: any) {
  const { data } = params;
  try {
    const role = data?.project_vendors_type?.name || "";

    return (
      <div className="w-full flex">
        <TruncatedTextWithTooltip text={role} />
        <div>
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch {
    return "";
  }
}

function InviteStatusText(props: {
  statusId: number;
  userId: string;
  inviteType: string | null;
  isCIQUser: boolean;
  context?: any;
  userData?: any;
  roleId?: any;
}) {
  const { statusId, userId, inviteType, isCIQUser, context, userData, roleId } =
    props;

  const { subscriptionRole } = useContext(SubscriptionContext);
  const { tokenContents, isProjectArchived } = useContext(ProjectContext);
  const { gqlClientForProject } = useContext(ProjectContext);
  const [cancelprogess, setCancelProgress] = useState(false);
  const [resendprogess, setResendProgress] = useState(false);

  const [resendAccProjectUserInviteMutation] = useMutation(
    MUTATION_SEND_ACC_PROJECT_LEVEL_EMAIL_INVITE,
    {
      variables: {
        user_id: userId,
        role_id: roleId,
        invited_user_company_id: userData?.company_id
      },
      client: gqlClientForProject
    }
  );

  const [resendAccSubscriptionUserInviteMutation] = useMutation(
    MUTATION_SEND_ACC_SUBSCRIPTION_LEVEL_EMAIL_INVITE,
    {
      variables: {
        user_id: userId,
        permission_id: roleId,
        invited_user_company_id: userData?.company_id
      }
    }
  );

  const [resendInviteMutation] = useMutation(MUTATION_RESEND_EMAIL_INVITE, {
    variables: {
      user_id: userId,
      invited_user_company_id: userData?.company_id
    },
    client: gqlClientForProject
  });

  const [resendSubscriptionInviteMutation] = useMutation(
    MUTATION_RESEND_EMAIL_INVITE,
    {
      variables: {
        user_id: userId,
        invited_user_company_id: userData?.company_id
      }
    }
  );
  const [cancelSubscriptionInviteMutattion] = useMutation<any>(
    MUTATION_CANCEL_SUBSCRIPTION_INVITATION,
    {
      variables: { user_id: userId }
    }
  );
  const [cancelProjectUserInviteMutattion] = useCIQMutation(
    MUTATION_CANCEL_PROJECT_USER_INVITATION,
    {
      variables: { user_id: userId },
      client: gqlClientForProject
    }
  );

  const resendInvite = () => {
    setResendProgress(true);

    if (inviteType === "Subscription") {
      (isCIQUser
        ? resendSubscriptionInviteMutation()
        : resendAccSubscriptionUserInviteMutation()
      )
        .then((res) => {
          if (res.data) {
            message.success(res.data.resend_email_invite.message);
          }
          if (res.errors) {
            message.error(res.errors[0].message);
          }
        })
        .finally(() => setResendProgress(false));
    } else {
      (isCIQUser
        ? resendInviteMutation()
        : resendAccProjectUserInviteMutation()
      )
        .then((res) => {
          if (res.data) {
            message.success(res.data.resend_email_invite.message);
          }
          if (res.errors) {
            message.error(res.errors[0].message);
          }
        })
        .finally(() => setResendProgress(false));
    }
  };

  const cancelSubscriptionInvite = () => {
    setCancelProgress(true);
    cancelSubscriptionInviteMutattion()
      .then((res) => {
        if (res.data) {
          message.success(res.data.cancel_subscription_invite.message);
        }
        if (res.errors) {
          message.error(res.errors[0].message);
        }
      })
      .finally(() => setCancelProgress(false));
  };

  const cancelProjectUserInvite = () => {
    setCancelProgress(true);
    cancelProjectUserInviteMutattion({
      variables: { user_id: userId }
    })
      .then((res) => {
        if (res.data) {
          message.success(res.data.cancel_project_invite.message);
        }
        if (res.errors) {
          message.error(res.errors[0].message);
        }
      })
      .finally(() => setCancelProgress(false));
  };

  switch (statusId) {
    case AcceptanceStatus.INVITED as number:
      return (
        <div className="flex space-x-2 items-baseline">
          <p className="margin-0 captialize-first text-red-600">Pending</p>
          {inviteType === "ProjectUser" &&
            tokenContents &&
            !isProjectArchived &&
            isGcProjectAdminRole(tokenContents.role) && (
              <div className=" space-x-2">
                <Button
                  loading={resendprogess}
                  disabled={resendprogess || cancelprogess}
                  onClick={resendInvite}
                  data-testid="index-button-pending-resend"
                >
                  Resend
                </Button>
                <Button
                  loading={cancelprogess}
                  disabled={resendprogess || cancelprogess}
                  onClick={cancelProjectUserInvite}
                  data-testid="index-button-cancel-invite"
                >
                  Cancel Invite
                </Button>
              </div>
            )}

          {inviteType === "Subscription" &&
            subscriptionRole >= RoleSubscriptionEnum.subscription_admin && (
              <div className=" space-x-2">
                <Button
                  loading={resendprogess}
                  disabled={resendprogess || cancelprogess}
                  onClick={resendInvite}
                  data-testid="index-button-subscription-admin-resend"
                >
                  Resend
                </Button>
                <Button
                  loading={cancelprogess}
                  disabled={resendprogess || cancelprogess}
                  onClick={cancelSubscriptionInvite}
                  data-testid="index-button-subscription-admin-cancel"
                >
                  Cancel Invite
                </Button>
              </div>
            )}
        </div>
      );
    case AcceptanceStatus.ACTIVE as number:
      return (
        <p
          className="margin-0 captialize-first text-green-600"
          data-testid="index-button-invite-status"
        >
          Active
        </p>
      );
    case AcceptanceStatus.DEACTIVATED as number:
      return (
        <p
          className="margin-0 captialize-first text-ciq-gray-950"
          data-testid="index-button-invite-status"
        >
          Inactive
        </p>
      );
    case AcceptanceStatus.NOT_INVITED as number:
      return (
        <div className="flex space-x-2 items-baseline">
          <p
            className="margin-0 captialize-first text-ciq-gray-950"
            data-testid="index-button-invite-status"
          >
            Not Invited
          </p>
          {inviteType === "Subscription" &&
            subscriptionRole >= RoleSubscriptionEnum.subscription_admin && (
              <Button
                data-testid="index-button-invite-subscription-admin"
                onClick={() => {
                  context?.setInviteAccUser({
                    showInviteModel: true,
                    inviteUserData: userData
                  });
                }}
              >
                Send Invite
              </Button>
            )}

          {inviteType === "ProjectUser" &&
            tokenContents &&
            !isProjectArchived &&
            isGcProjectAdminRole(tokenContents.role) && (
              <div className=" space-x-2">
                <Button
                  data-testid="index-button-invite-gc-project-admin"
                  onClick={() => {
                    context?.setInviteAccUser({
                      showInviteModel: true,
                      inviteUserData: userData
                    });
                  }}
                >
                  Send Invite
                </Button>
              </div>
            )}
        </div>
      );
    default:
      return null;
  }
}

export const projectUserActionRenderer = (params: any) => {
  const { data, context } = params;

  const infoMsg =
    data.status_id === AcceptanceStatus.ACTIVE
      ? "No permission to deactivate"
      : "No permission to activate";

  const confirmMsg =
    data.status_id === AcceptanceStatus.ACTIVE
      ? "Are you sure you want to deactivate this user from the project?"
      : "Are you sure you want to make this user active? ";

  const onToggleAction = (isChecked: boolean) => {
    modal.confirm({
      // The message used to be wrapped in a `pl-10` div. antd v4 took the
      // confirm icon out of flow (floated), so the content had to reserve room
      // for it by hand. v6 lays `.ant-modal-confirm-body` out as flex with the
      // icon as a real sibling and its own `marginInlineEnd`, so that 40px is
      // now added on top of the gap antd already provides — which is what
      // pushed the icon away from the text.
      content: confirmMsg,
      onOk() {
        context?.userAction(data?.user?.id, isChecked);
      },
      okText: "Confirm"
    });
  };

  if (
    data.status_id === AcceptanceStatus.INVITED ||
    context?.currentUser?.email === data?.user?.email
  ) {
    return "";
  }

  try {
    return (
      <div className="w-full h-full flex items-center custom-switch">
        <Tooltip
          title={
            !context?.isGCADmin && !context?.isProjectArchived ? infoMsg : ""
          }
        >
          <Switch
            disabled={!context?.isGCADmin}
            checked={data.status_id === AcceptanceStatus.ACTIVE}
            checkedChildren="ON"
            unCheckedChildren="OFF"
            onChange={(isChecked) => {
              onToggleAction(isChecked);
            }}
            data-testid="index-switch-user-action"
          />
        </Tooltip>
      </div>
    );
  } catch {
    return "";
  }
};

export function InviteStatusCellRenderer(props: any) {
  const { data, context } = props;
  const { value, inviteType } = props;
  let dataSources = [];
  let isCIQUser = true;

  let projectVendorType = null;
  let roleId = null;

  if (inviteType === "ProjectUser") {
    dataSources = getProjectUserSources(data);
    projectVendorType =
      data?.subscription_vendor &&
      data?.subscription_vendor?.project_vendors?.length &&
      data?.subscription_vendor?.project_vendors[0].project_vendors_type?.id;

    if (data?.project_role) {
      roleId = data?.project_role?.id;
    }
  } else {
    dataSources = getSubscriptionUserSources(data);
    roleId = data?.subscription_permission_id;
  }
  isCIQUser = dataSources.includes(SourceNames.CONSTRUCTIVIQ);
  const organizations = data.subscription?.organization_subscriptions;
  const org = organizations?.length ? organizations[0] : null;
  const company = data?.subscription_vendor ?? org?.organization;

  const userData = {
    email: data?.user?.email,
    phone: data?.user?.phone,
    id: data?.user?.id,
    first_name: data?.user?.first_name,
    last_name: data?.user?.last_name,
    company_id: company?.id,
    company_name: company?.name,
    type: projectVendorType
  };

  return (
    <div className="flex space-x-3">
      <div className="w-12">
        <InviteStatusText
          statusId={value}
          userId={data?.user_id}
          inviteType={inviteType}
          isCIQUser={isCIQUser}
          context={context}
          userData={userData}
          roleId={roleId}
        />
      </div>
      {inviteType === "ProjectUser" &&
        isCIQUser &&
        value !== AcceptanceStatus.NOT_INVITED && (
          <div className="-mt-3">{projectUserActionRenderer(props)}</div>
        )}
    </div>
  );
}

export function userOrgCellRenderer(
  params: any,
  disableTooltip?: boolean
): any {
  const { data } = params;
  const userInput =
    params.userInput || params.colDef.cellRendererParams.userInput;

  if (!data[userInput.id] && !data[userInput.unregistered_user])
    return {
      cellMarkup: (
        <div className="flex justify-end">
          {agGridCheckFieldEditable(params) && <EditableCell />}
        </div>
      ),
      fullName: ""
    };

  let fullName = "";
  let userOrg = "";
  if (data[userInput.id]) {
    fullName = `${data[userInput.firstName]} ${data[userInput.lastName]}`;
    userOrg = data[userInput.org];
  }
  if (data[userInput.unregistered_user]) {
    fullName = data[userInput.unregistered_user];
    userOrg = data[userInput.unregistered_user_org];
  }

  const statusId = data[userInput.statusKey];

  let cellMarkup = <div />;
  try {
    cellMarkup = (
      <div className="flex w-full max-w-full h-full items-center">
        <div className="w-full max-w-full overflow-hidden space-y-1">
          <TruncatedTextWithTooltip
            className="w-full max-w-full leading-none colFirstValue"
            text={fullName}
            disableTooltip={disableTooltip}
            tooltipContent={
              <TitleDescriptionTooltip title={fullName} description={userOrg} />
            }
          />
          <TruncatedTextWithTooltip
            className="w-full max-w-full leading-none colSecondValue"
            text={userOrg}
            disableTooltip={disableTooltip}
            tooltipContent={
              <TitleDescriptionTooltip title={fullName} description={userOrg} />
            }
          />
        </div>
        {agGridCheckFieldEditable(params) && (
          <div className="flex items-center pl-1">
            <EditableCell />
          </div>
        )}
        {statusId && statusId === AcceptanceStatus.DEACTIVATED ? (
          <div className="absolute flex w-full items-start justify-end pr-3 -mt-[10px]">
            <UserTag label="Inactive" />
          </div>
        ) : (
          ""
        )}
      </div>
    );
  } catch {
    //
  }

  return {
    cellMarkup,
    fullName,
    userOrg
  };
}

function LinkedActivitiesTooltip(props: {
  activity: {
    activity_id: string;
    task_name: string;
    reverse_dependency: boolean;
    critical?: boolean;
    start_date?: string;
    progress: number;
    total_slack: number;
    linked_to_end_date?: boolean;
    end_date?: string;
    actual_start_date?: string;
    actual_finish_date?: string;
    total_slack_computed?: any;
    offset_duration?: any;
    linked_offset_date?: any;
    deleted_from_source?: boolean;
  };
}) {
  const { activity } = props;
  const name = activity.activity_id
    ? `${activity.activity_id} - ${activity.task_name}`
    : activity.task_name;

  const getLabels = (label: string, value: string, valueClass: string = "") => {
    return (
      <div className="grid grid-cols-9 text-[10px] text-ciq-gray-700">
        <div className="col-span-4 font-medium">{label}</div>
        <div
          className={`flex items-center space-x-1 col-span-5 text-black text-opacity-80 ${valueClass}`}
        >
          <span className="print:truncate">{value}</span>
          {activity?.linked_offset_date &&
            (label === "Planned Start date:" ||
              label === "Planned End date:") && (
              <ActivityOffsetTag task={activity} isShowDate />
            )}
        </div>
      </div>
    );
  };

  const floatVal = activity?.total_slack
    ? Math.floor((activity?.total_slack || 0) / 8)
    : "";
  const isPermissionEnabled = activity?.total_slack_computed !== "restricted";
  const duration =
    activity?.offset_duration < 0
      ? activity?.offset_duration
      : `+${activity?.offset_duration}`;
  const popupContent = (
    <div className="min-w-[228px] max-w-[280px]">
      {activity.deleted_from_source && (
        <div className="w-full flex flex-col items-center justify-center border border-solid border-ciq-gray-300 rounded  text-xs">
          <div className="text-ciq-red-700 font-semibold bg-white px-1">
            Deleted
          </div>
          {/* <div className="italic">Relink if required.</div> */}
        </div>
      )}
      {isPermissionEnabled && activity.critical && <CriticalActivityTag />}
      <div className="text-xs font-medium mt-2 mb-2">{name}</div>
      {activity.linked_to_end_date
        ? getLabels(
            "Planned End date:",
            DateUtils.format(activity?.end_date, "MM-DD-YYYY")
          )
        : getLabels(
            "Planned Start date:",
            DateUtils.format(activity?.start_date, "MM-DD-YYYY")
          )}
      {isPermissionEnabled &&
        getLabels(
          "Activity Float:",
          floatVal !== "" ? `${floatVal} days` : "",
          activity?.critical
            ? "font-bold text-black text-opacity-80"
            : "font-medium"
        )}
      {activity?.actual_start_date &&
        getLabels(
          "Actual Start date:",
          DateUtils.format(activity?.actual_start_date, "MM-DD-YYYY")
        )}
      {activity?.progress > 0 &&
        activity?.progress < 100 &&
        !activity?.actual_finish_date &&
        getLabels("Progress:", `${activity?.progress}%`)}
      {activity?.actual_finish_date &&
        getLabels(
          "Actual End date:",
          DateUtils.format(activity?.actual_finish_date, "MM-DD-YYYY")
        )}
      {activity.reverse_dependency && (
        <div className="mt-2">
          {getLabels("Link Properties:", "")}
          <div className="flex items-center justify-center bg-ciq-gray-100 rounded h-6 space-x-1 px-2">
            <span className="flex items-center">
              <SwapOutlined className="text-ciq-gray-700" />
            </span>
            <span className="flex items-center font-normal text-xs">
              Activity is the Predecessor
            </span>
          </div>
        </div>
      )}

      {activity.offset_duration && (
        <div className="mt-2">
          {getLabels("Link Properties:", "")}
          <div className="flex items-center justify-center bg-ciq-gray-100 rounded h-5 space-x-1 px-2">
            <span className="flex items-center">
              <OffsetIcon />
            </span>
            <span className="flex items-center font-normal text-xs">
              Offset: {duration} Days
            </span>
          </div>
        </div>
      )}
    </div>
  );

  const activityIcon = () => {
    if (activity.deleted_from_source) {
      return (
        <span className="text-ciq-red-700 font-semibold px-1 text-xs">
          Deleted
        </span>
      );
    }
    if (
      isPermissionEnabled &&
      activity.critical &&
      activity.reverse_dependency
    ) {
      return (
        <span className="flex items-center">
          <span className="flex items-center bg-ciq-blue-300 p-1">
            <CriticalActivityIcon />
          </span>
          <span className="flex items-center p-1">
            <SwapOutlined className="text-ciq-gray-700" />
          </span>
        </span>
      );
    }
    if (isPermissionEnabled && activity.critical) {
      return (
        <span className="flex items-center bg-ciq-blue-300 p-[3px] mr-1">
          <CriticalActivityIcon />
        </span>
      );
    }

    if (activity.reverse_dependency) {
      return (
        <span className="flex items-center p-1">
          <SwapOutlined className="text-ciq-gray-700" />
        </span>
      );
    }

    if (activity.linked_offset_date) {
      return (
        <span className="flex items-center p-1">
          <OffsetIcon />
        </span>
      );
    }
    return (
      <span className="flex items-center p-1">
        <LinkOutlined className="fill-link-outlined" />
      </span>
    );
  };

  return (
    <Popover trigger="hover" content={popupContent}>
      <Tag
        color="default"
        className={`flex h-[54px] w-[160px] max-w-full m-0 space-x-0.5 items-start overflow-hidden py-[1.5px] px-1 print:h-auto ${
          isPermissionEnabled && activity.critical
            ? "border-ciq-blue-300"
            : "border-gray-300"
        } ${activity.deleted_from_source ? "bg-white" : ""}`}
      >
        <span className="flex items-center shrink-0">{activityIcon()}</span>
        <span className="min-w-0 whitespace-normal break-words leading-tight line-clamp-3 print:line-clamp-none print:truncate">
          {name}
        </span>
      </Tag>
    </Popover>
  );
}

export function TaskLinksCellRenderer(params: ICellRendererParams) {
  const { data, colDef } = params;
  const colId = colDef?.colId || colDef?.field;
  const tasks = sortAlphanumeric(data?.tasks || [], "activity_id").sort(
    (a, b) => {
      if (a.deleted_from_source && !b.deleted_from_source) return 1;
      if (!a.deleted_from_source && b.deleted_from_source) return -1;
      return 0;
    }
  );

  // Two cards inline, matching the Linked Submittals column.
  const countForShows = 2;

  const toShow = tasks.slice(0, countForShows);
  const remainingAll = tasks.slice(countForShows, tasks.length);

  const remainingPersisted = remainingAll.filter(
    (task: any) => !task.deleted_from_source
  );
  const remainingDeleted = remainingAll.filter(
    (task: any) => task.deleted_from_source
  );

  // max-w fits 2 x 160px card + gap; the cards carry their own fixed size, so
  // they look identical to the ones rendered in the grid row.
  const popupContentPersisted = (
    <div className="max-w-[336px] m-0 text-xs flex flex-wrap gap-2">
      {remainingPersisted.map((task: any) => {
        return <LinkedActivitiesTooltip activity={task} key={task?.task_id} />;
      })}
    </div>
  );

  const popupContentDeleted = (
    <div className="max-w-[336px] m-0 text-xs flex flex-wrap gap-2">
      {remainingDeleted.map((task: any) => {
        return <LinkedActivitiesTooltip activity={task} key={task?.task_id} />;
      })}
    </div>
  );

  return (
    <div
      /* items-start, not items-center: in the custom reports grid this cell can
         span several rows, and centring the card in a tall cell leaves a large
         gap above and below it. Top alignment matches the other columns. The
         count badges below carry the card's height so they stay centred against
         the card rather than riding up to the cell's top edge. */
      className="h-full py-1 flex items-start gap-x-2"
      data-testid={`${colId}`}
    >
      {toShow.map((task: any) => {
        return (
          <div className="flex print:truncate" key={task?.task_id}>
            <LinkedActivitiesTooltip activity={task} />
          </div>
        );
      })}

      {/* The count badges sit after the cards, not inside the loop, so they are
          rendered once for the whole cell. Plain "+N" text, same as the Linked
          Submittals and Linked Materials columns — no pill, no oversized "+".
          The deleted count keeps its icon, which is the only thing telling the
          two counts apart. */}
      {remainingPersisted.length ? (
        <div className="flex-none flex-grow-0 h-[54px] leading-none flex items-center">
          <Popover
            content={popupContentPersisted}
            title={<div className="py-1">Linked Activity(s)</div>}
          >
            <div>+{remainingPersisted.length}</div>
          </Popover>
        </div>
      ) : (
        ""
      )}

      {remainingDeleted.length ? (
        <div className="flex-none flex-grow-0 h-[54px] leading-none flex items-center">
          <Popover
            content={popupContentDeleted}
            title={<div className="py-1">Deleted Activity(s)</div>}
          >
            <div className="flex items-center">
              <DeleteOutlined />
              <span className="pl-0.5">{remainingDeleted.length}</span>
            </div>
          </Popover>
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

function LinkedMaterialTooltip(props: {
  material: {
    id: string;
    material_sequence_id: string;
    name: string;
    implicit: boolean;
    milestone_state: string;
    risk_level: string;
    spec_section: Array<{
      id: string;
      name: string;
      number: string;
    }>;
    predecessor_material?: boolean;
  };
}) {
  const { material } = props;

  const materialName = material.implicit
    ? material.name
    : `${material?.material_sequence_id} - ${material?.name}`;

  const row = (title: string, value: any, valueClassname = "") => (
    <div className="flex">
      <div className="w-12 text-ciq-gray-700">{title}</div>
      <div className={valueClassname}>{value}</div>
    </div>
  );
  const specSection = material?.spec_section?.length
    ? material?.spec_section[0]
    : null;

  const specSectionStr = specSectionLabel({
    specNumber: specSection?.number || "",
    specName: specSection?.name || ""
  });

  const linkRoleLabel = material?.predecessor_material
    ? "Material is the Predecessor"
    : material?.predecessor_material === false
      ? "Material is the Successor"
      : null;

  const popupContent = (
    <div className="min-w-[129px] max-w-[239px] text-black text-opacity-80">
      <div className="text-xs">{materialName}</div>
      <div className="text-[10px] pt-3 flex flex-col">
        <div className="flex-col min-w-0">
          <div className="text-ciq-gray-700">Spec Section:</div>
          <div className="break-all">{specSectionStr}</div>
        </div>
      </div>
      <div className="pt-3 flex flex-col text-[10px]">
        {row("Status:", material?.milestone_state)}
        {row(
          "Risk:",
          material?.risk_level,
          material?.risk_level === (RiskLevelType.High as string)
            ? "date-block-risk-high font-semibold"
            : ""
        )}
      </div>
      {linkRoleLabel && (
        <div className="mt-2">
          <div className="text-[10px] text-ciq-gray-700 font-medium">
            Link Properties:
          </div>
          <div className="flex items-center justify-center bg-ciq-gray-100 rounded h-6 px-2">
            <span className="flex items-center font-normal text-xs">
              {linkRoleLabel}
            </span>
          </div>
        </div>
      )}
    </div>
  );
  return (
    <Popover trigger="hover" content={popupContent}>
      {/* Same fixed 160 x 54 card as the Linked Activities / Linked Submittals
          columns — see LinkedActivitiesTooltip for why the height is explicit.
          The icon is a child rather than the `icon` prop so the name next to it
          can be a clamped block of its own. */}
      <Tag
        color="default"
        className={`flex h-[54px] w-[160px] max-w-full m-0 space-x-0.5 items-start overflow-hidden py-[1.5px] px-1 print:h-auto ${
          material?.risk_level === (RiskLevelType.High as string)
            ? "risk-high-box"
            : "bg-gray-100"
        }`}
      >
        {/* p-1 matches the padding LinkedActivitiesTooltip's activityIcon puts
            around its icons, so the icon-to-text gap is the same in both. */}
        <span className="flex items-center shrink-0 p-1">
          <LinkOutlined className="fill-link-outlined" />
        </span>
        <span className="min-w-0 whitespace-normal break-words leading-tight line-clamp-3 print:line-clamp-none print:truncate">
          {materialName}
        </span>
      </Tag>
    </Popover>
  );
}

export function MaterialLinksCellRenderer(params: ICellRendererParams) {
  const { data } = params;
  const value = data?.materials?.filter((m: any) => !m.implicit);

  const materials = sortLinkedItems(value || [], "material_sequence_id");
  const actualMaterials = materials.filter(
    (material: any) => !material.implicit
  );
  const toShow = actualMaterials.slice(0, 2);
  const remaining = actualMaterials.slice(2, actualMaterials.length);
  const isHighEntityExist = remaining.some(
    (entity: any) => entity?.risk_level === (RiskLevelType.High as string)
  );

  // max-w fits 2 x 160px card + gap; the cards carry their own fixed size, so
  // they look identical to the ones rendered in the grid row.
  const popupContent = (
    <div className="max-w-[336px] m-0 text-xs flex flex-wrap gap-2">
      {remaining.map((item: any) => {
        return (
          <Link
            key={item.id}
            to={`materials/${item.id}`}
            target="_blank"
            className="flex"
            data-testid="index-link-7"
          >
            <LinkedMaterialTooltip material={item} />
          </Link>
        );
      })}
    </div>
  );

  // items-start for the same reason as TaskLinksCellRenderer: this cell can span
  // several rows in the custom reports grid, and centring would leave a gap
  // above and below the card.
  return (
    <div className="h-full py-1 leading-none flex items-start gap-x-2">
      {toShow.map((material: any) => {
        return (
          <div className="flex" key={material.id}>
            <Link
              key={material.id}
              to={`materials/${material.id}`}
              target="_blank"
              className="flex"
              data-testid="index-link-linked-material"
            >
              <LinkedMaterialTooltip material={material} />
            </Link>
          </div>
        );
      })}

      {/* The count badge sits after the cards, not inside the loop, so it is
          rendered once for the whole cell. */}
      {remaining.length ? (
        <div className="flex-none flex-grow-0 h-[54px] flex items-center">
          <Popover
            content={popupContent}
            title={<div className="py-1">Linked Material(s)</div>}
          >
            {/* Red when a high-risk item is hidden behind the count, but the
                same weight as every other "+N" so the three link columns
                match. */}
            <div className={isHighEntityExist ? "date-block-risk-high" : ""}>
              +{remaining.length}
            </div>
          </Popover>
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

function LinkedSubmittalTooltip(props: {
  showSpecSectionNo: boolean;
  submittal: {
    id: string;
    submittal_sequence_id: string;
    submittal_id?: string;
    name: string;
    implicit: boolean;
    risk_level: string;
    workflow_status: number;
    status: number;
    type: string;
    revision: number;
    spec_no: string;
    spec_section_no?: string;
    spec_name: string;
    deleted_from_source: boolean;
  };
}) {
  const { submittal, showSpecSectionNo } = props;
  const sequenceId =
    submittal?.submittal_sequence_id || submittal?.submittal_id || "";
  const specNo = submittal?.spec_no || submittal?.spec_section_no;

  const submittalRefrenceId: string =
    showSpecSectionNo && specNo ? `${specNo} - ${sequenceId}` : sequenceId;
  const name = `${submittalRefrenceId} - ${submittal?.name}`;
  const status = SubmittalStatusToStrMap[submittal?.workflow_status];
  const specSectionStr = specSectionLabel({
    specNumber: specNo,
    specName: submittal?.spec_name
  });
  const row = (title: string, value: any, valueClassname = "") => (
    <div className="flex">
      <div className="w-12 text-ciq-gray-700">{title}</div>
      <div className={valueClassname}>{value}</div>
    </div>
  );

  const popupContent = (
    <div className="min-w-[129px] max-w-[239px] text-black text-opacity-80">
      {submittal.workflow_status === (ESubmittalStatus.VOID as number) && (
        <div className="pb-3">
          <div className="w-full p-1 bg-ciq-void-100 text-black text-opacity-50 flex items-center justify-center font-bold text-xs rounded">
            Void
          </div>
        </div>
      )}
      {submittal?.deleted_from_source && (
        <div className="pb-3">
          <div className="w-full p-1 bg-ciq-void-100 text-black text-opacity-50 flex items-center justify-center font-bold text-xs rounded">
            Deleted
          </div>
        </div>
      )}
      <div className="text-xs">{name}</div>
      <div className="text-[10px] pt-3 flex flex-col">
        <div className="flex-col min-w-0">
          <div className="text-ciq-gray-700">Spec Section:</div>
          <div className="break-all">{specSectionStr}</div>
        </div>
      </div>

      <div className="pt-3 flex flex-col text-[10px]">
        {row("Type:", submittal?.type)} {row("Revision:", submittal?.revision)}
      </div>
      <div className="pt-3 flex flex-col text-[10px]">
        {row("Status:", status)}{" "}
        {row(
          "Risk:",
          submittal?.deleted_from_source ? "" : submittal?.risk_level,
          submittal?.risk_level === (RiskLevelType.High as string)
            ? "date-block-risk-high font-semibold"
            : ""
        )}
      </div>
    </div>
  );
  const getClass = () => {
    switch (true) {
      case submittal?.deleted_from_source:
        return "w-[120px] truncate bg-ciq-blue-50 border-ciq-void-200";
      case submittal.workflow_status === (ESubmittalStatus.VOID as number):
        return "w-[120px] truncate bg-ciq-void-100 border-ciq-void-200";
      case submittal?.risk_level === (RiskLevelType.High as string):
        return "w-[120px] truncate risk-high-box";
      default:
        return "bg-gray-100 w-[120px] truncate";
    }
  };
  return (
    <Popover trigger="hover" content={popupContent}>
      {submittal?.deleted_from_source ? (
        <Tag color="default" className={getClass()}>
          <span className="text-black text-opacity-50 font-bold text-xs mr-2">
            Deleted
          </span>
          <span className="text-black text-opacity-30 font-normal text-xs">
            {name}
          </span>
        </Tag>
      ) : (
        <Tag
          color="default"
          icon={
            submittal.workflow_status === (ESubmittalStatus.VOID as number) ? (
              <VoidSubmittalIcon type="text" />
            ) : (
              <LinkOutlined className="fill-link-outlined" />
            )
          }
          className={getClass()}
        >
          {name}
        </Tag>
      )}
    </Popover>
  );
}

type TLinkedSubmittal = {
  id: string;
  feature_type: number;
  sequence_id: number;
  name: string;
  milestone_state: string;
  risk_level?: RiskLevelType;
  workflow_status: EWorkflowStatusDataBlock;
  deleted_from_source: boolean;
  type: any;
  revision?: number;
  spec_section: Array<{
    id: string;
    name: string | null;
    number: string | null;
  }>;
  milestones: Array<{
    actual_date: string | null;
    name: string | null;
    projected_date: string | null;
    planned_date: string | null;
    sequence_no: number;
    milestone_action: string | null;
    milestone_type: {
      id: number;
      name: string;
    };
  }>;
};

function LinkedSubmittalEntityWithPopover(props: {
  showSpecSectionNo: boolean;
  submittal: TLinkedSubmittal;
}) {
  const { submittal, showSpecSectionNo } = props;

  const sequenceId = submittal?.sequence_id || "";
  const specSection = submittal.spec_section?.length
    ? submittal.spec_section[0]
    : null;
  const specSectionStr = specSectionLabel({
    specNumber: specSection?.number,
    specName: specSection?.name
  });

  const submittalRefrenceId: string =
    showSpecSectionNo && specSection
      ? `${specSection.number} - ${sequenceId}`
      : `${sequenceId}`;
  const name = `${submittalRefrenceId} - ${submittal?.name}`;
  const status = submittal.milestone_state;

  const submittalWFStarted = useMemo(() => {
    return (
      submittal?.workflow_status === EWorkflowStatusDataBlock.IN_PROGRESS ||
      submittal?.workflow_status === EWorkflowStatusDataBlock.WF_COMPLETED
    );
  }, [submittal]);

  const isVoidSubmittal = useMemo(
    () => submittal.workflow_status === EWorkflowStatusDataBlock.VOID,
    [submittal]
  );

  const isDeletedSubmittal = useMemo(
    () => submittal.deleted_from_source,
    [submittal]
  );

  const submittalWFStatusUsingDateBlock = useMemo(() => {
    const subcontractorMilestone = submittal.milestones?.find(
      (x) => x.sequence_no === 2
    );
    const designReviewMilestone = submittal.milestones?.find(
      (x) => x.sequence_no === 4
    );

    const submitDate =
      subcontractorMilestone?.actual_date ||
      (submittalWFStarted
        ? subcontractorMilestone?.projected_date
        : subcontractorMilestone?.planned_date);

    const designReviewDate =
      designReviewMilestone?.actual_date ||
      (submittalWFStarted
        ? designReviewMilestone?.projected_date
        : designReviewMilestone?.planned_date);

    return {
      submit_by_subcontractor: {
        label: subcontractorMilestone?.name || "",
        date: submitDate ? DateUtils.format(submitDate, "MM-DD-YY") : "",
        milestoneCompleted: !!subcontractorMilestone?.actual_date
      },
      design_review_complete: {
        label: designReviewMilestone?.name || "",
        date: designReviewDate
          ? DateUtils.format(designReviewDate, "MM-DD-YY")
          : "",
        milestoneCompleted: !!designReviewMilestone?.actual_date
      }
    };
  }, [submittal, submittalWFStarted]);

  const row = useCallback(
    (title: string, value: any, valueClassname = "") => (
      <div className="flex">
        <div className="w-12 text-ciq-gray-700">{title}</div>
        <div className={valueClassname}>{value}</div>
      </div>
    ),
    []
  );

  const getStatusIcon = useCallback(
    (dateStr: string, milestoneCompleted: boolean) => {
      if (dateStr) {
        return milestoneCompleted ? <TickIcon /> : <ClockIcon />;
      }
      return "";
    },
    []
  );

  const wfStatusRow = useCallback(
    (
      title: string = "",
      value: any = "",
      milestoneCompleted: boolean = false
    ) => (
      <>
        <div className="text-ciq-gray-700">{title}:</div>
        <div className={value ? "text-right" : "text-left"}>{value || "-"}</div>
        <div className="w-[10px] flex justify-center items-center">
          {getStatusIcon(value, milestoneCompleted)}
        </div>
      </>
    ),
    [getStatusIcon]
  );

  const popupContent = (
    <div className="text-black text-opacity-80 max-w-[239px]">
      {isVoidSubmittal && (
        <div className="pb-3">
          <div className="w-full p-1 bg-ciq-void-100 text-black text-opacity-50 flex items-center justify-center font-bold text-xs rounded">
            Void
          </div>
        </div>
      )}

      {isDeletedSubmittal && (
        <div className="w-full pb-2">
          <DeletedSubmittalTag />
        </div>
      )}
      <div className="text-xs font-medium mb-3 break-all">
        {submittal?.sequence_id} - {submittal?.name}
      </div>
      <div className="text-[10px] space-y-3">
        <div className="min-w-0">
          <div className="text-ciq-gray-700">Spec Section:</div>
          <div className="break-all">{specSectionStr}</div>
        </div>
        <div>
          {row("Type:", submittal?.type)}
          {row("Revision:", submittal?.revision)}
        </div>
        {!isVoidSubmittal && !isDeletedSubmittal && (
          <>
            <div>
              {row("Status:", status)}
              {row(
                "Risk:",
                submittal?.risk_level,
                submittal?.risk_level === RiskLevelType.High
                  ? "date-block-risk-high font-semibold"
                  : ""
              )}
            </div>
            <div>
              <div className="grid grid-cols-[max-content_auto_12px] gap-x-1">
                {wfStatusRow(
                  submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                    ?.label,
                  submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                    ?.date,
                  submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                    ?.milestoneCompleted
                )}

                {wfStatusRow(
                  submittalWFStatusUsingDateBlock?.design_review_complete
                    ?.label,
                  submittalWFStatusUsingDateBlock?.design_review_complete?.date,
                  submittalWFStatusUsingDateBlock?.design_review_complete
                    ?.milestoneCompleted
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );

  const conditionalClasses = useMemo(() => {
    switch (true) {
      case isVoidSubmittal:
        return "bg-ciq-gray-100 border-ciq-void-200";
      case isDeletedSubmittal:
        return "bg-ciq-gray-100 border-ciq-void-200";
      case submittal?.risk_level === RiskLevelType.High:
        return "risk-high-box";
      default:
        return "bg-ciq-gray-50 border-black border-opacity-10";
    }
  }, [isVoidSubmittal, isDeletedSubmittal, submittal?.risk_level]);

  const linkIcon = useMemo(() => {
    return isVoidSubmittal ? (
      <VoidSubmittalIcon type="text" />
    ) : (
      <LinkOutlined />
    );
  }, [isVoidSubmittal]);

  return (
    <Popover trigger="hover" content={popupContent}>
      {isDeletedSubmittal ? (
        <Tag
          color="default"
          className={`flex rounded-xs items-center w-full max-w-full m-0 py-[1.5px] ${conditionalClasses}`}
        >
          <div className="w-full max-w-full py-0.5 mb-0.5 overflow-hidden leading-normal text-wrap whitespace-normal break-words">
            <span className="mr-1 align-middle text-black text-opacity-50 font-bold text-xs">
              Deleted
            </span>
            <span className="text-xs text-wrap font-normal text-black text-opacity-50">
              {name}
            </span>
          </div>
        </Tag>
      ) : (
        <Tag
          color="default"
          className={`w-full max-w-full m-0 py-[1.5px] ${conditionalClasses}`}
        >
          {!isVoidSubmittal ? (
            <>
              <div className="w-full max-w-full overflow-hidden truncate leading-none py-0.5 mb-0.5 print:truncate">
                <span className="mr-1 align-middle">{linkIcon}</span>
                <span className="text-xs font-semibold align-middle print:truncate">
                  {name}
                </span>
              </div>
              <div className="space-y-0.5">
                <div className="text-xs align-middle leading-none flex">
                  <div className="w-[60px]">Submitted:</div>
                  <div
                    className={`grow ${
                      submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                        ?.date
                        ? "text-right"
                        : "pl-1.5"
                    }`}
                  >
                    {submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                      ?.date || "-"}
                  </div>
                  <div className="pl-1 min-w-[10px]">
                    {getStatusIcon(
                      submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                        ?.date,
                      submittalWFStatusUsingDateBlock?.submit_by_subcontractor
                        ?.milestoneCompleted
                    )}
                  </div>
                </div>

                <div className="text-xs align-middle leading-none flex">
                  <div className="w-[60px]">Approved:</div>
                  <div
                    className={`grow ${
                      submittalWFStatusUsingDateBlock?.design_review_complete
                        ?.date
                        ? "text-right"
                        : "pl-1.5"
                    }`}
                  >
                    {submittalWFStatusUsingDateBlock?.design_review_complete
                      ?.date || "-"}
                  </div>
                  <div className="pl-1 min-w-[10px]">
                    {getStatusIcon(
                      submittalWFStatusUsingDateBlock?.design_review_complete
                        ?.date,
                      submittalWFStatusUsingDateBlock?.design_review_complete
                        ?.milestoneCompleted
                    )}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <div className="w-full max-w-full py-0.5 mb-0.5 overflow-hidden leading-normal text-wrap whitespace-normal break-words">
              <span className="mr-1 align-middle">
                {isVoidSubmittal && <VoidSubmittalIcon type="text" />}
              </span>
              <span className="text-xs text-wrap font-semibold">{name}</span>
            </div>
          )}
        </Tag>
      )}
    </Popover>
  );
}

const sortFeatureSubmittalLinkedItems = (items: Array<TLinkedSubmittal>) => {
  const riskOrder = {
    HIGH: 3,
    MEDIUM: 2,
    LOW: 1,
    blank: 0
  };
  return items.sort((a, b) => {
    const aVoid = a.workflow_status === EWorkflowStatusDataBlock.VOID;
    const bVoid = b.workflow_status === EWorkflowStatusDataBlock.VOID;

    // 1. Priority: VOID first
    if (aVoid && !bVoid) return -1;
    if (!aVoid && bVoid) return 1;

    // 2. Priority: risk_level (HIGH > MEDIUM > LOW > blank)
    const riskDiff =
      riskOrder[b.risk_level ?? "blank"] - riskOrder[a.risk_level ?? "blank"];
    if (riskDiff !== 0) return riskDiff;

    // 3. Priority: sequence_id ascending
    return a.sequence_id - b.sequence_id;
  });
};

export function SubmittalLinksEntityCellRenderer(params: ICellRendererParams) {
  const { data, context } = params;
  const showSpecSectionNo = context.projectDetails?.spec_section;

  const linkedItems = (data?.linked_items || []) as Array<TLinkedSubmittal>;

  const submittals = sortFeatureSubmittalLinkedItems(
    linkedItems?.filter((x: TLinkedSubmittal) => x.feature_type === 1)
  );

  if (submittals.length === 0) {
    const plannedSubmittal = linkedItems?.find(
      (x: TLinkedSubmittal) =>
        x.feature_type === (FeatureTypes.PLANNING_ONLY_SUBMITTAL as number)
    );
    if (plannedSubmittal) {
      const submittedMilestone = plannedSubmittal.milestones?.find(
        (m) => m.milestone_type?.name === "Submission"
      );
      const approvedMilestone = plannedSubmittal.milestones?.find(
        (m) => m.milestone_type?.name === "Design Review"
      );
      return (
        <div className="h-full flex flex-col w-40 text-xs bg-white border border-dashed border-ciq-gray-300 rounded px-2 py-1 justify-center">
          <div className="w-full text-black text-opacity-60 font-semibold flex justify-center">
            Submittal Plan
          </div>
          <div className="w-full flex justify-between text-ciq-gray-975">
            <div>Submitted : </div>
            <div>
              {submittedMilestone?.planned_date
                ? DateUtils.format(submittedMilestone?.planned_date)
                : "-"}
            </div>
          </div>
          <div className="w-full flex justify-between text-ciq-gray-975">
            <div>Approved : </div>
            <div>
              {approvedMilestone?.planned_date
                ? DateUtils.format(approvedMilestone?.planned_date)
                : "-"}
            </div>
          </div>
        </div>
      );
    }
  }

  const toShow = submittals.slice(0, 2);
  const remaining = submittals.slice(2, submittals.length);

  const isHighEntityExist = remaining.some(
    (entity: any) => entity?.risk_level === (RiskLevelType.High as string)
  );

  const popupContent = (
    <div className="m-0 text-xs grid grid-cols-2 gap-y-2 gap-x-2">
      {remaining.map((item: any) => {
        return (
          <div key={item.id}>
            <Link
              key={item.id}
              target="_blank"
              to={`submittals/${item.id}`}
              className="w-[160px] h-[56px] flex items-stretch"
              data-testid="index-link-linked-submittal-entity-with-popover"
            >
              <LinkedSubmittalEntityWithPopover
                submittal={item}
                showSpecSectionNo={showSpecSectionNo}
              />
            </Link>
          </div>
        );
      })}
    </div>
  );

  return (
    <div className="h-full py-0.5 flex items-stretch gap-x-2">
      {toShow.map((submittal: any) => {
        return (
          <div
            className="flex items-stretch space-x-2 print:truncate"
            key={submittal.id}
          >
            <Link
              key={submittal.id}
              target="_blank"
              to={`submittals/${submittal.id}`}
              className="w-[160px] leading-normal flex items-stretch"
              data-testid="index-link-linked-submittal-entity-with-popover-2"
            >
              <LinkedSubmittalEntityWithPopover
                submittal={submittal}
                showSpecSectionNo={showSpecSectionNo}
              />
            </Link>
          </div>
        );
      })}
      {submittals.length > 2 ? (
        <div className="flex-none flex-grow-0 leading-none flex items-center">
          <Popover
            content={popupContent}
            title={<div className="py-1">Linked Submittal(s)</div>}
          >
            {/* Same treatment as the Linked Materials / Linked Activities
                counts: risk colour only, no bold. */}
            <div className={isHighEntityExist ? "date-block-risk-high" : ""}>
              +{remaining.length}
            </div>
          </Popover>
        </div>
      ) : (
        ""
      )}
    </div>
  );
}

export const responsibleContractorCellRenderer = (params: any): any => {
  const { data } = params;
  let cellMarkup = <div />;

  try {
    cellMarkup = (
      <div className="w-full flex items-center">
        <TruncatedTextWithTooltip
          text={data.responsible_contractor_name || data.trade_partner_name}
        />
        <div className="px-1">
          <EditOutlined className="cell-edit-icon" />
        </div>
      </div>
    );
  } catch {
    return "";
  }

  return {
    cellMarkup,
    orgName: data?.responsible_contractor_name || data.trade_partner_name || ""
  };
};

export function gcRepresentativeCellRendererWithCustomEdit(params: any): any {
  const { data, context } = params;
  const onClickEdit = () => {
    context.onEditCell({
      rowIndex: params.node.rowIndex!,
      colKey: params.column!.getId(),
      data
    });
  };
  const userInput =
    params.userInput || params.colDef.cellRendererParams.userInput;
  if (!data[userInput.id])
    return {
      cellMarkup: (
        <button
          type="button"
          className="flex justify-end w-full border-0 bg-transparent"
          onClick={onClickEdit}
          data-testid="index-button-gc-representative"
        >
          {agGridCheckFieldEditable(params) && <EditableCell />}
        </button>
      ),
      fullName: ""
    };

  let fullName = "";
  if (data[userInput.id]) {
    fullName = `${data[userInput.firstName]} ${data[userInput.lastName]}`;
  }

  const statusId = data[userInput.statusKey];

  let cellMarkup = <div />;
  try {
    cellMarkup = (
      <button
        type="button"
        className="w-full flex border-0 bg-transparent text-left items-center"
        onClick={onClickEdit}
        data-testid="index-button-gc-representative-custom-edit"
      >
        {statusId && statusId === AcceptanceStatus.DEACTIVATED ? (
          <div className="absolute flex w-full items-start justify-end pr-4 -top-2.5">
            <UserTag label="Inactive" />
          </div>
        ) : (
          ""
        )}
        <div className="grow overflow-hidden">
          <TruncatedTextWithTooltip text={fullName} />
        </div>
        <div>{agGridCheckFieldEditable(params) && <EditableCell />}</div>
      </button>
    );
  } catch {
    //
  }

  return {
    cellMarkup,
    fullName
  };
}

export function gcRepresentativeCellRenderer(params: any): any {
  const { data } = params;
  const userInput =
    params.userInput || params.colDef.cellRendererParams.userInput;
  if (!data[userInput.id])
    return {
      cellMarkup: (
        <div className="flex justify-end">
          {agGridCheckFieldEditable(params) && <EditableCell />}
        </div>
      ),
      fullName: ""
    };

  let fullName = "";
  if (data[userInput.id]) {
    fullName = `${data[userInput.firstName]} ${data[userInput.lastName]}`;
  }

  let cellMarkup = <div />;
  try {
    cellMarkup = (
      <div className="flex items-start">
        <div>
          <p className="colFirstValue">{fullName}</p>
        </div>
        {agGridCheckFieldEditable(params) && <EditableCell />}
      </div>
    );
  } catch {
    //
  }

  return {
    cellMarkup,
    fullName
  };
}

export function SubmittalReportDetailsCellRenderer(params: any) {
  const { data } = params;
  const submittalData = { ...data };

  submittalData.submittal_schedule_links =
    submittalData.submittal_schedule_links || [];
  submittalData.submittal_material_links =
    submittalData.submittal_material_links?.map((m: any) => ({
      assignee: m.assignee,
      driving_material: m.driving_material,
      material_id: m.id,
      material_sequence_id: m.material_id,
      material_name: m.name,
      status: m.status,
      implicit: m.implicit,
      material: {
        date_block_materials: [
          {
            planned_milestone_1: m.ROJ_date,
            name_milestone_1: m.last_milestone_name
          }
        ]
      }
    })) || [];

  const governingActivity = useMemo(() => {
    const linkedTask = submittalData?.submittal_schedule_links?.find(
      (task: any) => task.driving_task
    );
    if (linkedTask)
      return {
        ...linkedTask.gantt_task,
        text: linkedTask.task_name,
        isLinkedEndDate: linkedTask.linked_to_end_date
      };

    return {} as any;
  }, [submittalData?.submittal_schedule_links]);

  return (
    <div>
      <DateBlockSubmittalImpactVisualisation
        submittal={submittalData}
        governingTask={governingActivity}
      />
    </div>
  );
}

export function UpcomingSubmittalReportDetailsCellRenderer(params: any) {
  const { data } = params;
  const submittalData = { ...data };
  const { projectId } = useParams() as any;

  submittalData.submittal_schedule_links =
    submittalData.submittal_schedule_links || [];
  submittalData.submittal_material_links =
    submittalData.submittal_material_links?.map((m: any) => ({
      assignee: m.assignee,
      driving_material: m.driving_material,
      material_id: m.id,
      material_sequence_id: m.material_id,
      material_name: m.name,
      status: m.status,
      implicit: m.implicit,
      material: {
        date_block_materials: [
          {
            planned_milestone_1: m.ROJ_date,
            name_milestone_1: m.last_milestone_name
          }
        ]
      },

      risk_level: m.risk_level,
      milestone_state: m.milestone_state,
      spec_section_name: m.spec_section_name,
      spec_section_no: m.spec_section_no,
      spec_section: [
        {
          id: m?.spec_section_id,
          name: m?.spec_section_name,
          number: m?.spec_section_no
        }
      ]
    })) || [];

  const governingActivity = useMemo(() => {
    const linkedTask = submittalData?.submittal_schedule_links?.find(
      (task: any) => task.driving_task
    );
    if (linkedTask)
      return {
        ...linkedTask.gantt_task,
        text: linkedTask.task_name,
        isLinkedEndDate: linkedTask.linked_to_end_date
      };

    return {} as any;
  }, [submittalData?.submittal_schedule_links]);

  return (
    <div className="flex flex-col w-[98%]">
      <div className="flex font-medium space-x-2 pt-3 pl-10">
        <div className="px-2">Impacted Materials:</div>
        <div className="flex flex-wrap gap-2">
          {submittalData?.submittal_material_links?.length ? "" : "None"}
          {submittalData?.submittal_material_links?.map((item: any) => {
            let materialName = "";
            if (item.implicit) {
              materialName = `Material for Submittal ${submittalData.submittal_id}`;
            } else {
              materialName = item.material_name;
            }
            const tag = (
              <LinkedMaterialTooltip
                material={{ ...item, name: materialName }}
              />
            );
            return item.implicit ? (
              tag
            ) : (
              <Link
                to={`/project/${projectId}/materials/${item.material_id}`}
                target="_blank"
                key={materialName}
                data-testid="index-link-upcoming-submittal-report"
              >
                {tag}
              </Link>
            );
          })}
        </div>
      </div>

      <div className="flex font-medium space-x-2 pt-4 pl-10">
        <div className="px-2">Impacted Activities:</div>
        <div className="flex flex-wrap gap-2">
          {submittalData?.submittal_schedule_links?.length ? "" : "None"}
          {submittalData?.submittal_schedule_links?.map((item: any) => {
            return <LinkedActivitiesTooltip activity={item} key={item.id} />;
          })}
        </div>
      </div>
      <DateBlockSubmittalImpactVisualisation
        submittal={submittalData}
        governingTask={governingActivity}
      />
    </div>
  );
}

export function UpcomingMaterialReportDetailsCellRenderer(params: any) {
  const { data, context } = params;
  const { projectId } = useParams() as any;
  const materialData = { ...data };
  const showSpecSectionNo = context.projectDetails?.spec_section;

  materialData.material_material_links =
    materialData?.material_material_links?.map((item: any) => {
      const specSection = {
        id: item?.spec_section?.spec_section_id,
        name: item?.spec_section?.spec_section_name,
        number: item?.spec_section?.spec_section_no
      };
      return {
        ...item,
        spec_section: [specSection]
      };
    });

  return (
    <div className="flex flex-col w-[98%] pb-3">
      <div className="flex font-medium space-x-2 pt-3 pl-10">
        <div className="px-2">Linked Submittals:</div>
        <div className="flex flex-wrap gap-2">
          {materialData?.material_submitttal_links?.length ? "" : "None"}
          {materialData?.material_submitttal_links?.map((item: any) => {
            return (
              <Link
                key={item.id}
                to={`/project/${projectId}/submittals/${item.id}`}
                target="_blank"
                className="leading-normal"
                data-testid="index-link-upcoming-material-report"
              >
                <LinkedSubmittalTooltip
                  submittal={item}
                  showSpecSectionNo={showSpecSectionNo}
                />
              </Link>
            );
          })}
        </div>
      </div>
      <div className="flex font-medium space-x-2 pt-3 pl-10">
        <div className="px-2">Impacted Materials:</div>
        <div className="flex flex-wrap gap-2">
          {materialData?.material_material_links?.length ? "" : "None"}
          {materialData?.material_material_links?.map((item: any) => {
            let materialName = "";
            if (item.implicit) {
              materialName = `Material for Submittal ${materialData.submittal_id}`;
            } else {
              materialName = item.material_name || item.name;
            }
            const tag = (
              <LinkedMaterialTooltip
                material={{ ...item, name: materialName }}
              />
            );
            return item.implicit ? (
              tag
            ) : (
              <Link
                to={`/project/${projectId}/materials/${item.material_id}`}
                target="_blank"
                key={materialName}
                data-testid="index-link-upcoming-submittal-report"
              >
                {tag}
              </Link>
            );
          })}
        </div>
      </div>

      <div className="flex font-medium space-x-2 pt-4 pl-10">
        <div className="px-2">Impacted Activities:</div>
        <div className="flex flex-wrap gap-2">
          {materialData?.material_schedule_links?.length ? "" : "None"}
          {materialData?.material_schedule_links?.map((item: any) => {
            return <LinkedActivitiesTooltip activity={item} key={item.id} />;
          })}
        </div>
      </div>
    </div>
  );
}

export const nextDeadlineCellRenderer = (
  params: any,
  disableTooltip: boolean
) => {
  if (!params.value) return null;
  const colId = params.colDef?.colId || params.colDef?.field;
  const todayStartDateObject = DateUtils.dateTimeObj().startOf("day");
  // valueGetter formats as MM-DD-YYYY; pass that format so Safari does not
  // rely on native Date.parse (which rejects MM-DD-YYYY).
  const nextDeadlineDateObject = DateUtils.dateTimeObj(
    params.value,
    DATE_FORMAT_MMDDYYYY
  );
  const isPast = nextDeadlineDateObject.isBefore(todayStartDateObject);
  const cellValueClassnames = isPast ? "date-block-date-risk" : "";
  return (
    <div className="flex items-center">
      <TruncatedTextWithTooltip
        text={params.value}
        className={cellValueClassnames}
        disableTooltip={disableTooltip}
        dataTestId={colId}
      />
    </div>
  );
};

export const featureTemplateListNameCellRenderer = (params: any) => {
  const { data, context } = params;

  return (
    <div className="w-full">
      <div className="flex space-x-3 min-w-0">
        <div className="shrink overflow-hidden text-ellipsis">
          <button
            type="button"
            data-testid="index-button-feature-template-name"
            className="bg-transparent border-0 underlined-text cursor-pointer whitespace-pre"
            onClick={() => {
              context.onTemplateNameClick(data);
            }}
          >
            {params.value}
          </button>
        </div>
        {data.default && (
          <div>
            <DefaultTag />
          </div>
        )}
      </div>
    </div>
  );
};

export function FeatureTemplateListActionsCellRenderer(params: any) {
  const { data, context } = params;
  const {
    onWorkflowCopy,
    savingDefaultTemplate,
    canEditFeatureDBTemplate,
    featureTemplates
  } = context;
  const [copyPopupVisible, setCopyPopupVisible] = useState(false);

  const isTemplateDisabled = data.disabled;
  const isTemplateUsed =
    !!data.feature_workflow_instances_aggregate?.aggregate?.count;

  const availableTemplateNames = useMemo(() => {
    return featureTemplates.map((template: any) => {
      return template.name.toLowerCase();
    });
  }, [featureTemplates]);

  const editDisableTooltip = useMemo(() => {
    if (!canEditFeatureDBTemplate) {
      return ErrorMessages.PermissionNotGranted;
    }

    return "";
  }, [canEditFeatureDBTemplate]);

  const isCopyDisabled =
    savingDefaultTemplate || !canEditFeatureDBTemplate || isTemplateDisabled;

  return (
    <div className="flex items-center">
      <Button
        disabled={
          savingDefaultTemplate ||
          !canEditFeatureDBTemplate ||
          isTemplateDisabled
        }
        title={editDisableTooltip}
        type="text"
        size="small"
        className="-mt-0.5 border-0"
        onClick={() => {
          context.onTemplateEditClick(data);
        }}
        data-testid="index-button-feature-template-list-actions"
      >
        <EditOutlined />
      </Button>
      {!isCopyDisabled ? (
        <Popover
          open={copyPopupVisible}
          placement="bottom"
          content={
            <CopyDBTemplate
              setCopyPopupVisible={setCopyPopupVisible}
              availableTemplateNames={availableTemplateNames}
              template={data}
              onCopy={onWorkflowCopy}
            />
          }
          trigger="click"
          destroyOnHidden
          onOpenChange={() => {
            setCopyPopupVisible(!copyPopupVisible);
          }}
        >
          <Button
            type="text"
            size="small"
            className="pt-0.5 border-0"
            onClick={() => {
              setCopyPopupVisible(true);
            }}
            data-testid="index-button-copy"
          >
            <CopyIcon />
          </Button>
        </Popover>
      ) : (
        <Button
          disabled
          type="text"
          size="small"
          className="pt-0.5 border-0"
          title={
            !canEditFeatureDBTemplate ? ErrorMessages.PermissionNotGranted : ""
          }
        >
          <CopyIcon />
        </Button>
      )}

      <Switch
        className="-mt-0.5 ml-1.5"
        checked={!isTemplateDisabled}
        data-testid="index-switch-template-enabled"
        onChange={(value: any) => {
          context.onToggleTemplateDisabledConfirm(data, value);
        }}
        size="small"
        disabled={!canEditFeatureDBTemplate || savingDefaultTemplate}
        title={
          !canEditFeatureDBTemplate ? ErrorMessages.PermissionNotGranted : ""
        }
      />
      {isTemplateUsed && (
        <div className="ml-2">
          <Tag className="whitespace-pre-line break-words leading-tight mt-[10px]">
            Template in use. Actions limited.
          </Tag>
        </div>
      )}
    </div>
  );
}

export const featureTemplateListDefaultCellRenderer = (params: any) => {
  const { data, context } = params;

  const { savingDefaultTemplate, canEditFeatureDBTemplate } = context;

  const isTemplateDisabled = data.disabled;

  return (
    <div className="flex items-center">
      <Radio.Group
        disabled={
          savingDefaultTemplate ||
          !canEditFeatureDBTemplate ||
          isTemplateDisabled
        }
        onChange={() => {
          context.onTemplateDefaultChange(data);
        }}
        value={data.default ? 1 : 0}
      >
        <Radio className="ml-5" value={1} />
      </Radio.Group>
    </div>
  );
};

export const integrationTypeCellRenderer = (params: any) => {
  const { value } = params;
  const integrationSystemName = IntegrationSystemNameMap[value];

  const sourceLogo = () => {
    switch (value) {
      case IntegrationType[IntegrationType.PROCORE]:
        return <ProcoreLogoIcon />;
      case IntegrationType[IntegrationType.AUTODESK]:
        return <AutodeskLogoIcon />;
      case IntegrationType[IntegrationType.BIM360]:
        return (
          <img src={BIM360logo} alt="" style={{ width: 18, height: 19 }} />
        );
      case OPC_INTEGRATION_SYSTEM:
        return <OpcLogoIcon />;
      default:
        return null;
    }
  };

  return (
    <div className="flex items-center space-x-2" title={integrationSystemName}>
      <div className="flex items-center">{sourceLogo()}</div>
      <TruncatedTextWithTooltip text={integrationSystemName} />
    </div>
  );
};

export function IntegrationNameCellRenderer(params: any) {
  const { context, data } = params;

  const canManageIntegration = canOpenIntegrationDetails(data);

  const subReauthRequired =
    data.properties.SUBSCRIPTION_CONFIG_AUTH_STATE &&
    data.properties.SUBSCRIPTION_CONFIG_AUTH_STATE ===
      IntegrationAuthStates.REAUTHORIZATION_REQUIRED;

  const projReauthRequired =
    data.properties.PROJECT_CONFIG_AUTH_STATE &&
    data.properties.PROJECT_CONFIG_AUTH_STATE ===
      IntegrationAuthStates.REAUTHORIZATION_REQUIRED;

  const reauthRequired = subReauthRequired || projReauthRequired;

  return (
    <div className="max-w-full flex items-center">
      <div className="grow truncate flex items-center">
        {canManageIntegration ? (
          <button
            type="button"
            className="gridButtonLink gridButtonLink--integration-name"
            onClick={async (event) => {
              event.stopPropagation();
              context.onManageIntegrationClick(data);
            }}
            data-testid="index-button-integration-name"
          >
            <div className="space-x-1">
              <span>{data.properties.INTEGRATION_NAME}</span>
            </div>
          </button>
        ) : (
          <TruncatedTextWithTooltip text={data.properties.INTEGRATION_NAME} />
        )}
      </div>
      <div className="shrink-0 flex items-end">
        {reauthRequired && (
          <div className=" mt-2">
            <Popover
              placement="left"
              content={
                <div className="w-[300px]">
                  Authorization has expired and integration with{" "}
                  {`${IntegrationSystemNameMap[data.integration.system]}`} is no
                  longer valid. View configuration details to renew the
                  connection.
                </div>
              }
            >
              <div className="inline">
                <AuthenticationExpiredIcon />
              </div>
            </Popover>
          </div>
        )}
      </div>
    </div>
  );
}

export const dlGrpUserNameCellRenderer = (params: any): any => {
  const { data } = params;

  let cellDiv = <div />;
  try {
    cellDiv = (
      <div className="flex">
        <TruncatedTextWithTooltip text={data.name} />
        {data.status_id && data.status_id === AcceptanceStatus.DEACTIVATED ? (
          <div className="absolute flex w-full items-start justify-end pr-3 -top-2">
            <UserTag label="Inactive" />
          </div>
        ) : (
          ""
        )}
      </div>
    );
  } catch {
    //
  }

  return cellDiv;
};

export const userNameCellRenderer = (params: any) => {
  const { data } = params;
  const userInfo = data?.created_by_user ?? data?.user_created;

  const fullName = `${userInfo?.first_name ?? ""} ${userInfo?.last_name ?? ""}`;
  const email = userInfo?.email ?? "";

  const userDetails = (
    <div className="flex flex-col ">
      <div className="flex items-center h-5">{fullName}</div>
      <div className="flex items-center h-5">{email}</div>
    </div>
  );

  return userDetails;
};

export const riskCellRenderer = (
  { value, data, colDef }: any,
  disableTooltip: boolean
) => {
  if (!value) return "";
  if (data?.workflow_status === (ESubmittalStatus.VOID as number)) return "";
  const colId = colDef?.colId || colDef?.field;

  let cssColor = "";
  switch (value) {
    case RiskLevelType.High:
      cssColor = "bg-ciq-red-50 border-ciq-red-400 text-ciq-red-800";
      break;
    case RiskLevelType.Medium:
      cssColor = "bg-ciq-orange-50 border-ciq-orange-300 text-ciq-orange-750";
      break;
    default:
      cssColor = "bg-ciq-blue-100 border-ciq-blue-300 text-ciq-blue-700";
      break;
  }

  return (
    <div className="text-xs flex flex-wrap items-start h-full p-1">
      <div
        className={`border border-solid capitalize rounded px-2 py-1 ${cssColor}`}
      >
        <TruncatedTextWithTooltip
          text={(value || "").toLowerCase()}
          disableTooltip={disableTooltip}
          dataTestId={colId}
        />
      </div>
    </div>
  );
};

export const missingInSyncCellRenderer = ({ value }: any) => {
  if (value) {
    return (
      <div className="flex items-center">
        <MissingIcon />
      </div>
    );
  }
  return "";
};

export const materialSourceCellRenderer = (params: any) => {
  const { context, data } = params;
  const { history, projectDetails, isMaterialExtractionEnabled } = context;
  return (
    <div className="flex items-center">
      <TruncatedTextWithTooltip text={data.source} />
      {isMaterialExtractionEnabled && data.extraction_datum?.id ? (
        <Button
          size="small"
          ghost
          onClick={() => {
            history.replace(
              `/project/${projectDetails.id}/materials/extracted`,
              {
                name: "Extraction Data",
                extraction: data.extraction_datum
              }
            );
          }}
        >
          View
        </Button>
      ) : null}
    </div>
  );
};

export const materialActionCellRenderer = (params: any) => {
  const { context, data } = params;
  const {
    onLinkClick,
    onRequestDurationClick,
    canRequestLeadTime,
    isRequestLeadTimeFeatureEnabled,
    canInlineEditMaterialGeneralInfoAndParticipants,
    canViewLinkingPage,
    onCommentClick,
    isProjectArchived
  } = context;

  // The link icon only opens the linking page, which archived projects keep
  // viewable, so there it follows view access instead of edit access.
  const showLinkButton = isProjectArchived
    ? !!canViewLinkingPage
    : !!canInlineEditMaterialGeneralInfoAndParticipants;

  const linkButton = (
    <button
      type="button"
      className="ciq-icon-button !p-1.5"
      onClick={() => {
        onLinkClick(params.data);
      }}
      data-testid="index-button-material-action-v2"
    >
      <LinkOutlined />
    </button>
  );

  const commentCount = data?.no_of_comments ?? 0;
  const hasComments = commentCount > 0;
  const commentTooltip = hasComments
    ? `Comments (${commentCount})`
    : "Add Comment";

  const commentsIconButton = (
    <button
      type="button"
      className="ciq-icon-button !p-1.5"
      aria-label={commentTooltip}
      onClick={() => {
        onCommentClick({
          sourceRowIndex: params?.node?.sourceRowIndex
        });
      }}
      data-testid="action-button-material-comments"
    >
      <CommentIcon hasComments={hasComments} />
    </button>
  );
  const commentsButton = isProjectArchived ? (
    commentsIconButton
  ) : (
    <Tooltip title={commentTooltip} placement="top">
      {commentsIconButton}
    </Tooltip>
  );

  return (
    <div className="flex items-center justify-end">
      {showLinkButton ? linkButton : null}
      <MaterialRequestDurationButton
        material={data}
        isFeatureEnabled={isRequestLeadTimeFeatureEnabled}
        canRequest={canRequestLeadTime}
        onClick={onRequestDurationClick}
        isReadOnly={!!isProjectArchived}
      />
      {commentsButton}
    </div>
  );
};

export const featureCommentActionCellRenderer = (params: any) => {
  const { context, data } = params;
  const { onCommentClick, isCommentsReadOnly, isProjectArchived } = context;

  const commentCount = data?.no_of_comments ?? 0;
  const hasComments = commentCount > 0;
  let commentTooltip = isCommentsReadOnly ? "No Comments" : "Add Comment";
  if (hasComments) commentTooltip = `Comments (${commentCount})`;

  const commentsIconButton = (
    <button
      type="button"
      className="ciq-icon-button !p-1.5"
      aria-label={commentTooltip}
      onClick={() => {
        onCommentClick({
          sourceRowIndex: params?.node?.sourceRowIndex
        });
      }}
      data-testid="action-button-feature-comments"
    >
      <CommentIcon hasComments={hasComments} />
    </button>
  );

  return (
    <div className="flex h-full w-full items-center justify-center">
      {isProjectArchived ? (
        commentsIconButton
      ) : (
        <Tooltip title={commentTooltip} placement="top">
          {commentsIconButton}
        </Tooltip>
      )}
    </div>
  );
};

export function ActionsCellRenderer(params: any) {
  const { data, context, node, api } = params;
  const gridApi = api as GridApi;
  const {
    projectId,
    history,
    isViewLinkingPage,
    isGroupMaterialCreationEnabled,
    onSubmittalEditClick,
    openMoveUntrackedModal,
    cannotCreateMaterial,
    isProjectArchived
  } = context;

  const isAnyLinkedItems =
    (data?.linked_items?.filter((item: any) => !item.implicit).length ?? 0) >
      0 ||
    (data?.tasks?.filter((task: any) => !task.deleted_from_source)?.length ??
      0) > 0;

  const isDisabledUntrack =
    data.workflow_status?.id === ESubmittalStatus.VOID ||
    isAnyLinkedItems ||
    !!data?.date_block?.final_deadline_date;

  return (
    <div className="flex space-x-1 w-full items-center">
      {isViewLinkingPage ? (
        <Tooltip title="Manage Links">
          <Button
            key="submittal-link-button"
            className="icon-link-arrow"
            icon={<LinkIconLogPage />}
            type="link"
            onClick={() => {
              history.push(
                `/project/${projectId}/schedule/submittal-linking?&SubmittalId=${data.id}`
              );
            }}
            data-testid="submittal-link-button"
          />
        </Tooltip>
      ) : null}
      {data?.redirect_url &&
        (isProjectArchived ? (
          // icon-link-arrow forces the brand color, so it is dropped to let
          // the disabled link styling show.
          <Button
            key="submittal-redirect-button"
            className="button-spacing"
            icon={<ArrowUpBox />}
            color="default"
            type="link"
            disabled
            data-testid="submittal-redirect-button"
          />
        ) : (
          <Tooltip placement="right" title="View in Source">
            <Button
              key="submittal-redirect-button"
              className="icon-link-arrow button-spacing"
              icon={<ArrowUpBox />}
              color="default"
              type="link"
              onClick={() => {
                window.open(data?.redirect_url, "_blank");
              }}
              data-testid="submittal-redirect-button"
            />
          </Tooltip>
        ))}
      {isGroupMaterialCreationEnabled && !cannotCreateMaterial && (
        <Tooltip title="Create Material">
          <Button
            key="extract_create_material"
            color="default"
            type="link"
            className="icon-link-arrow"
            icon={
              <MaterialCreateCiqIcon
                size={16}
                className="fill-ciq-primary-900"
                opacity="1"
              />
            }
            data-testid="create-material-from-submittal-action-button"
            onClick={() => {
              gridApi?.deselectAll();
              node.setSelected(true);
              onSubmittalEditClick("extract_material");
            }}
          />
        </Tooltip>
      )}

      {!cannotCreateMaterial && !data.deleted_from_source && (
        <Dropdown
          menu={{
            items: [
              {
                key: "move-to-untracked",
                label: (
                  <Tooltip
                    title={
                      isDisabledUntrack
                        ? "Remove Links/Deadline to stop tracking"
                        : undefined
                    }
                    placement="left"
                  >
                    <span className="inline-flex items-center space-x-2">
                      <SubmittalUntrackCiqIcon
                        size={16}
                        className="fill-black shrink-0"
                        opacity="0.45"
                      />
                      <span className="leading-none">Do not track</span>
                    </span>
                  </Tooltip>
                ),
                disabled: isDisabledUntrack, // this should be set based on your logic/context
                onClick: () => {
                  if (!isDisabledUntrack) {
                    openMoveUntrackedModal?.(data ? [data] : []);
                  }
                }
              }
            ]
          }}
          trigger={["click"]}
          placement="bottomLeft"
        >
          <Button
            key="submittal-move-to-untracked-button"
            data-testid="submittal-move-to-untracked-button"
            className="icon-link-arrow"
            icon={<MoreOutlined className="mb-1" />}
            color="default"
            type="link"
          />
        </Dropdown>
      )}
    </div>
  );
}

export function ActionsUnTrackedSubmittalCellRenderer(params: any) {
  const { data, context } = params;

  const { openMoveTrackedModal, cannotEditUnTracked } = context;

  return (
    <div className="flex space-x-1 w-full items-center">
      {!cannotEditUnTracked && (
        <Tooltip title="Track Submittal">
          <Button
            key="submittal-track-button"
            data-testid="submittal-track-button"
            className="icon-link-arrow button-spacing"
            icon={<SubmittalMoveToTrackedIcon />}
            color="default"
            type="link"
            onClick={() => {
              openMoveTrackedModal?.(data ? [data] : []);
            }}
          />
        </Tooltip>
      )}

      {data?.redirect_url && (
        <Tooltip placement="right" title="View in Source">
          <Button
            key="submittal-redirect-button"
            className="icon-link-arrow button-spacing"
            icon={<ArrowUpBox />}
            color="default"
            type="link"
            onClick={() => {
              window.open(data?.redirect_url, "_blank");
            }}
            data-testid="submittal-redirect-button"
          />
        </Tooltip>
      )}
    </div>
  );
}

export const logPageIDLinkCellRenderer = (id: string, url: string) => {
  return (
    <Link className="gridIdLink" to={url} data-testid="index-link-log-page-id">
      <TruncatedTextWithTooltip text={id} />
    </Link>
  );
};

export const criticalActivityIndicatorCellRenderer = (params: any) => {
  const { data } = params;
  if (data?.critical && data?.type !== GanttActivityTypes.Wps) {
    return (
      <div className="flex w-full h-full items-center justify-start ml-1.5">
        <Tooltip title="Critical Activity">
          <CriticalActivityIndicator />
        </Tooltip>
      </div>
    );
  }
  return "";
};
export const dummyColumnDef = {
  colId: "ag-Grid-SelectionColumn",
  headerName: "",
  width: 20,
  maxWidth: 20,
  minWidth: 20,
  resizable: false,
  filter: false,
  sortable: false,
  suppressHeaderMenuButton: true,
  suppressColumnsToolPanel: true
} as ColDef;

export const dummyColumnDef2 = {
  colId: "ag-Grid-SelectionColumn",
  headerName: "",
  width: 40,
  maxWidth: 40,
  minWidth: 40,
  resizable: false,
  filter: false,
  sortable: false,
  suppressHeaderMenuButton: true,
  suppressColumnsToolPanel: true
} as ColDef;

export function LBSCellRenderer(params: any) {
  const { value, context, node, column, data } = params;

  const selectedLocations: { id: string; path: string }[] =
    value && value.length > 0
      ? [...value].sort((a, b) => a.path.localeCompare(b.path))
      : [];

  const toShow = selectedLocations.slice(0, 2);
  const remaining = selectedLocations.slice(2, selectedLocations.length);

  const locationTag = (location: any) => {
    return (
      <Tag color="default" key={location.id} title={location.path}>
        {location.path}
      </Tag>
    );
  };

  const popoverContent = (locations: any[]) => {
    return (
      <div className="max-w-[300px] max-h-[300px] overflow-y-auto flex flex-wrap items-center gap-y-1 gap-x-0 leading-none py-2">
        {locations.map((location: any) => locationTag(location))}
      </div>
    );
  };

  return (
    <CellChilderenRendererWithEditButton
      onEditCell={() => {
        context.onEditCell?.({
          rowIndex: node.rowIndex!,
          colKey: column!.getId(),
          data
        });
      }}
    >
      <div className="h-full max-w-full max-h-full min-w-[250px] overflow-hidden py-1 flex items-end">
        <div className="flex flex-wrap gap-y-1 gap-x-0 leading-none items-center overflow-hidden">
          {toShow.map((location: any, index: number) => {
            if (index !== toShow.length - 1) {
              return locationTag(location);
            }
            return (
              <div className="flex" key={location.id}>
                {locationTag(location)}
                {remaining.length > 0 && (
                  <Popover content={popoverContent(remaining)}>
                    <div className="py-1 text-left">+{remaining.length}</div>
                  </Popover>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </CellChilderenRendererWithEditButton>
  );
}
